import React, { useCallback, useRef, useState } from "react";
import { Button, CircularProgress, LinearProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadIcon from "@mui/icons-material/Upload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import FileUploadIcon from "@/assets/file-upload.svg";
import { preflightStaffIngress } from "@/services/staffIngress";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL, BASE_API_URL_V2 } from "@/constants";
import useOrgId from "@/components/FpAppV4/hooks/useOrgId";
import clsx from "clsx";
import { queryClient } from "@/ReactQuery/reactQueryClient";
import { readDroppedFolder } from "./utils/readDroppedFolder";
import useFileTrayStore from "./FileTree/UploadStuff/useFileTrayStore";
import {
  clearProductInterval,
  setProductInterval,
} from "@/utils/productTimers";

interface FileUploadV3Props {
  workspaceId: string;
  /** Target folder path segments (empty for root). */
  prependFilePath?: string[];
  hideFileTray?: boolean;
  onComplete?: () => void;
  onFileCompleted?: (resp: SuccessfulUpload, file: File) => void;
  useFilesystem?: boolean;
  selectables?: ("file" | "folder")[];
  multiple?: boolean;
}

export default function FileUploadV3({
  workspaceId,
  prependFilePath = [],
  hideFileTray = false,
  onComplete,
  onFileCompleted,
  useFilesystem = true,
  selectables = ["file", "folder"],
  multiple = true,
}: FileUploadV3Props) {
  const orgId = useOrgId();
  const { isUploading, startUpload, status, error } = useBulkUploader({
    workspaceId,
    orgId,
    baseDirectoryPath: prependFilePath,
    onComplete,
    onFileCompleted,
    useFilesystem,
  });

  return (
    <div className="select-none">
      <div>
        {isUploading && (
          <div className="my-2">
            <span>Uploading files... ({status.remainingFiles} left)</span>
            <br />
            <span> Please do not refresh or exit the page.</span>
            <br />
            <QueuedUploadFile
              filename={status.file.name}
              bytes={status.file.size}
            />
            <LinearProgress variant="determinate" value={status.progress} />
          </div>
        )}
        {error && (
          <div className="my-2">
            <span className="text-wrap">
              Error while uploading file {error.file.name}:{" "}
              {error.error.toString()}
            </span>
          </div>
        )}

        <UploadArea
          uploading={isUploading}
          onUpload={startUpload}
          selectables={selectables}
          multiple={multiple}
        />
      </div>
    </div>
  );
}

interface UploadAreaProps {
  onUpload: (args: UploadCallbackParams) => void;
  uploading: boolean;
  selectables: ("file" | "folder")[];
  multiple: boolean;
}

function UploadArea({
  onUpload,
  uploading,
  selectables,
  multiple,
}: UploadAreaProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const handleRemoveFile = useCallback((index: number) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  }, []);

  const appendSelectedFiles = useCallback((files: File[]) => {
    // Filter out hidden files (starting with .)
    files = files.filter((file) => !file.name.startsWith("."));
    setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
  }, []);

  const onFileCompleted = useCallback((file: File) => {
    (window as any).openFpToast(
      `File ${file.name} uploaded successfully`,
      "success"
    );
    setSelectedFiles((prevFiles) => prevFiles.filter((f) => f !== file));
  }, []);

  const onFileFailed = useCallback((file: File, error: Error) => {
    (window as any).openFpToast(
      `File ${file.name} failed to upload: ${error}`,
      "error"
    );
  }, []);

  const listedFiles = uploading ? [] : selectedFiles;

  return (
    <>
      <div className="w-full max-h-[200px] overflow-auto px-2 mt-4">
        {listedFiles.map((file, index) => (
          <QueuedUploadFile
            key={index}
            filename={file.name}
            bytes={file.size}
            onDeselect={() => handleRemoveFile(index)}
          />
        ))}
      </div>

      {selectedFiles.length > 0 && (
        <div className="w-full flex justify-center mt-4 mb-4">
          <Button
            variant="contained"
            color="primary"
            onClick={() =>
              onUpload({
                files: selectedFiles,
                completed: onFileCompleted,
                failed: onFileFailed,
              })
            }
            disabled={uploading}
            startIcon={
              uploading ? <CircularProgress size={20} /> : <UploadIcon />
            }
            sx={{
              fontSize: "10px",
              padding: "8px 12px",
            }}
          >
            {uploading
              ? "Uploading..."
              : `Upload ${selectedFiles.length} Files`}
          </Button>
        </div>
      )}

      <div className="p-2">
        <ChooseInputContainer
          onFiles={appendSelectedFiles}
          selectables={selectables}
          multiple={multiple}
        />
        <DragDropArea
          onFiles={appendSelectedFiles}
          selectables={selectables}
          multiple={multiple}
        />
      </div>
    </>
  );
}

interface QueuedUploadFileProps {
  filename: string;
  bytes: number;
  onDeselect?: () => void;
}

function QueuedUploadFile({
  filename,
  bytes,
  onDeselect,
}: QueuedUploadFileProps) {
  return (
    <div className="p-2 bg-gray-200 mt-2 rounded-md flex items-center justify-between text-[10px] w-full ">
      <div className="flex items-center">
        <div className="max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap">
          {filename}
        </div>{" "}
        ({(bytes / 1024 / 1024).toFixed(2)} MB)
      </div>

      {onDeselect && (
        <CloseIcon
          onClick={onDeselect}
          style={{
            color: "red",
            fontSize: "14px",
            cursor: "pointer",
          }}
        />
      )}
    </div>
  );
}

type SelectionKind = "none" | "file" | "folder";

interface ChooseInputContainerProps {
  onFiles: (files: File[]) => void;
  selectables: ("file" | "folder")[];
  multiple?: boolean;
}

const ChooseInputContainer = ({
  onFiles,
  selectables,
  multiple = true,
}: ChooseInputContainerProps) => {
  const [kind, setKind] = useState<SelectionKind>("none");

  return (
    <div className="flex w-full my-2 text-[12px] justify-around">
      {selectables.includes("file") && (
        <ChooseInput
          onFiles={onFiles}
          selected={kind === "file"}
          onSelect={() => setKind("file")}
          inputProps={{ multiple: multiple }}
          Icon={UploadFileIcon}
          text={multiple ? "Choose Files" : "Choose File"}
        />
      )}
      {selectables.includes("folder") && (
        <ChooseInput
          onFiles={onFiles}
          selected={kind === "folder"}
          onSelect={() => setKind("folder")}
          inputProps={{
            webkitdirectory: "",
            directory: "",
          }}
          Icon={DriveFolderUploadIcon}
          text="Choose Folder"
        />
      )}
    </div>
  );
};

interface ChooseInputProps {
  onFiles: (files: File[]) => void;
  selected: boolean;
  onSelect: () => void;
  inputProps: JSX.IntrinsicElements["input"];
  Icon: React.ComponentType<{ style: React.CSSProperties }>;
  text: string;
}

const ChooseInput = ({
  onFiles,
  selected,
  onSelect,
  inputProps,
  Icon,
  text,
}: ChooseInputProps) => {
  const { ref, Component } = useHiddenInput(onFiles, inputProps);

  return (
    <>
      {Component}
      <div
        className={clsx(
          `ml-4 p-1 px-2 cursor-pointer rounded-md flex items-center transition-all`,
          selected && false
            ? "text-white bg-gray-700 border border-gray-700"
            : "text-gray-600 border hover:text-gray-800 hover:border-gray-500"
        )}
        onClick={() => {
          onSelect();
          if (!ref.current) throw new Error("Input ref not found");
          ref.current.click();
        }}
      >
        <Icon style={{ fontSize: "14px", marginRight: "5px" }} />
        {text}
      </div>
    </>
  );
};

const useHiddenInput = (
  onFiles: (files: File[]) => void,
  inputProps: JSX.IntrinsicElements["input"]
) => {
  const ref = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files;
      if (!fileList) return;

      const files = [...fileList];
      onFiles(files);
    },
    [onFiles]
  );

  const Component = (
    <input
      type="file"
      ref={ref}
      onChange={handleFileChange}
      style={{ display: "none" }}
      {...inputProps}
    />
  );

  return { ref, Component };
};

interface DragDropAreaProps {
  onFiles: (files: File[]) => void;
  selectables: ("file" | "folder")[];
  multiple: boolean;
}

const DragDropArea = ({
  onFiles,
  selectables,
  multiple,
}: DragDropAreaProps) => {
  const [dragActive, setDragActive] = useState(false);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      const items = e.dataTransfer.items;

      if (items) {
        const entries = Array.from(items).map((item) =>
          item.webkitGetAsEntry()
        );
        const hasFolder = entries.some((entry) => entry && entry.isDirectory);

        if (hasFolder) {
          // Handle folder drop
          readDroppedFolder(items).then((files) => {
            onFiles(files);
          });
        } else {
          // Handle file drop
          console.log("passing e.dataTransfer", e.dataTransfer.files);
          const files = e.dataTransfer.files;
          onFiles(Array.from(files));
        }
      }
    },
    [onFiles]
  );

  const { ref, Component } = useHiddenInput(onFiles, { multiple: true });

  return (
    <div className="w-full p-4">
      {Component}
      <div
        onDragEnter={() => setDragActive(true)}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        style={{
          border: dragActive ? "2px dashed #2196f3" : "2px dashed #aaaaaa",
          backgroundColor: dragActive ? "#e3f2fd" : "#eee",
        }}
        className="flex justify-center flex-col items-center w-full h-[200px] rounded-md p-4 text-gray-600 cursor-auto"
        onClick={() => ref.current?.click()}
      >
        <img src={FileUploadIcon} alt="" className="w-[30px] opacity-80" />
        <div className="mt-4 w-[300px] flex justify-center">
          {(() => {
            const key = selectables.join("-") + "-" + multiple.toString();
            switch (key) {
              case "file-true":
                return "Drop Files here";
              case "file-false":
                return "Drop File here";
              case "folder-true":
              case "folder-false":
                return "Drop Folder here";
              case "file-folder-true":
                return "Drop Files or Folder here";
              case "file-folder-false":
                return "Drop File or Folder here";
              default:
                return "Drop Files here";
            }
          })()}
        </div>
      </div>
    </div>
  );
};

interface UploadStatus {
  file: File;
  progress: number; // 0.0 to 1.0
  remainingFiles: number;
}

interface UploadCallbackParams {
  files: File[];
  completed: (file: File) => void;
  failed: (file: File, error: Error) => void;
}

export interface SuccessfulUpload {
  /** Task ID */ _id: string;
  blob_id: string;
  filename: string;
}

interface UseBulkUploaderParams {
  workspaceId: string;
  orgId?: string;
  /** Target folder path segments (empty for root). */
  baseDirectoryPath?: string[];
  onComplete?: () => void;
  onFileCompleted?: (resp: SuccessfulUpload, file: File) => void;
  onFail?: () => void;
  useFilesystem?: boolean;
}

export const useBulkUploader = (args: UseBulkUploaderParams) => {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<{
    file: File;
    remaining: number;
  } | null>();
  const [bytesUploaded, setBytesUploaded] = useState(0);
  const [error, setError] = useState<{ error: Error; file: File } | null>(null);

  /**
   * Process files in batches
   * @param files Array of files to upload
   * @param batchSize Number of files to process in each batch
   * @param processFn Function to process each batch item
   * @returns Results from all processed items
   */
  const processBatches = async <T, R>(
    items: T[],
    batchSize: number,
    processFn: (item: T, index: number, total: number) => Promise<R>
  ): Promise<R[]> => {
    const results: R[] = [];

    // Process files in batches
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);

      // Create array of promises to process each item in the batch
      const batchPromises = batch.map((item, batchIndex) => {
        const remainingItems = items.length - (i + batchIndex);
        return processFn(item, i + batchIndex, remainingItems);
      });

      // Wait for all items in this batch to complete before moving to the next batch
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
    }

    return results;
  };

  const startUpload = useCallback(
    async ({ files, completed, failed }: UploadCallbackParams) => {
      setError(null);
      setIsUploading(true);

      let hadError = false;

      // One staff ingress check for the whole batch (#10718): a refusal
      // the staff member declined fails every file and sends nothing.
      const preflight = await preflightStaffIngress(
        "document",
        files.map((file) => ({
          name:
            file.webkitRelativePath ||
            (file as File & { _customRelativePath?: string })
              ._customRelativePath ||
            file.name,
          file,
        }))
      );
      if (!preflight.proceed) {
        const refused = new Error("Not added");
        for (const file of files) failed(file, refused);
        setError({ error: refused, file: files[0] });
        if (args.onFail) args.onFail();
        setProgress(null);
        setBytesUploaded(0);
        setIsUploading(false);
        return;
      }

      // Define batch size
      const BATCH_SIZE = 10;

      const webUploadBatchSize = files.length;

      // Process the files in batches
      await processBatches(
        files,
        BATCH_SIZE,
        async (file, index, remaining) => {
          setBytesUploaded(0);
          setProgress({ file, remaining });

          // Stopped in the finally: a failed upload used to leave its
          // progress running for the life of the page (#12187).
          const stopProgress = simulateUploadProgress(setBytesUploaded);
          try {
            const resp = await uploadFile({
              ...args,
              file,
              batchSize: webUploadBatchSize,
              ingressBatch: preflight.batch,
            });
            if (resp?.duplicate) {
              // The modal is driven by the tray store — uploadFile() already
              // pushed this duplicate. Treat as completed so no error toast fires.
              completed(file);
              return resp;
            }
            if (!resp?._id) {
              throw new Error(
                "An error occurred while uploading the file (unable to get task id)"
              );
            }
            completed(file);
            if (args.onFileCompleted) {
              args.onFileCompleted(resp as SuccessfulUpload, file);
            }
            return resp;
          } catch (err) {
            //@ts-expect-error Not handling the edge case here
            const error: Error = err;
            failed(file, error);
            setError({ error, file });
            hadError = true;
            // Continue on uploading files even if one fails, as for a batch job we want to upload as much as possible
            return { error, file };
          } finally {
            stopProgress();
          }
        }
      );

      if (!hadError && args.onComplete) {
        args.onComplete();
      }

      // Show not yet fully processed files in gray now, but do it here, since if we do it earlier we will cause the UI pane for the file upload to disappear.
      queryClient.invalidateQueries({
        queryKey: ["orgFileList"],
      });

      setProgress(null);
      setBytesUploaded(0);
      setIsUploading(false);
    },
    [args]
  );

  if (isUploading === true) {
    if (!progress) throw new Error("Invariant broken: progress not set");

    const status: UploadStatus = {
      file: progress.file,
      progress: bytesUploaded / progress.file.size,
      remainingFiles: progress.remaining,
    };

    return {
      startUpload: () => alert("Cannot start another upload yet!"),
      isUploading: true as const,
      status,
      error,
    };
  }

  return {
    startUpload,
    isUploading: false as const,
    status: null,
    error,
  };
};

/** Milliseconds per step of the simulated upload progress. */
export const UPLOAD_PROGRESS_STEP_MS = 100;
/** Milliseconds the simulated progress holds at zero before it moves. */
export const UPLOAD_PROGRESS_HOLD_MS = 300;
/** Bytes the simulated progress adds per step: 1 MB/s. */
export const UPLOAD_PROGRESS_STEP_BYTES =
  1_000_000 / (1000 / UPLOAD_PROGRESS_STEP_MS);

/**
 * Hack to make it look like stuff is getting uploaded: the request reports no
 * progress of its own, so a bar advances at a nominal 1 MB/s. One interval,
 * whose step count says both when the bar starts to move and how far it has
 * gone; it used to be a sleep for the hold followed by a sleep per step
 * (#12187). Returns the stop, which the caller runs however the upload ends.
 */
export function simulateUploadProgress(
  update: (bytes: number) => void
): () => void {
  const holdSteps = UPLOAD_PROGRESS_HOLD_MS / UPLOAD_PROGRESS_STEP_MS;
  let steps = 0;
  const timer = setProductInterval(() => {
    steps += 1;
    if (steps <= holdSteps) return;
    update((steps - holdSteps) * UPLOAD_PROGRESS_STEP_BYTES);
  }, UPLOAD_PROGRESS_STEP_MS);
  return () => clearProductInterval(timer);
}

type UploadFileParams = {
  orgId?: string;
  workspaceId?: string;
  /** Folder path segments the file goes under (empty for root). Sent as a
   *  JSON array so a "/" in a folder name survives. */
  baseDirectoryPath?: string[];
  file: File;
  useFilesystem?: boolean;
  batchSize: number;
  driveId?: string; // Add driveId parameter
  asyncUpload?: boolean; // Add asyncUpload parameter for async uploads
  /** Attach the document we already hold instead of refusing the upload as a
   *  duplicate (#11242). For chat, where the user is handing over a file to be
   *  read rather than filing one: there is nothing to file twice, so "we have
   *  those bytes" should resolve to that document, not fail. Filing surfaces
   *  (Documents, RMS) leave this off and keep the 409 + duplicate modal. */
  reuseDuplicate?: boolean;
  /** The staff ingress batch token from a clean preflight (#10718): the
   *  server lets the file through unread, the preflight saw the batch. */
  ingressBatch?: string | null;
};

interface UploadFileResp {
  _id?: string;
  blob_id?: string;
  filename?: string;
  duplicate?: boolean;
  existing_path?: string[];
  existing_name?: string;
  /** The document this upload resolved to, when it matched one we already held
   *  and the caller asked for reuse. Absent on an ordinary upload. */
  duplicate_of?: string | null;
  /** Whether the returned document has finished processing. A reused document
   *  is normally already done, and its UNGREY_FILE_UPLOAD websocket message has
   *  long since been sent — without this the caller would wait forever for one
   *  that never comes, which is what left the chip spinning (#11242). */
  processed?: boolean;
  /** Whether that document's processing failed (#13887): it is still ready
   *  (`processed`), and the chip says its processing failed. */
  processing_failed?: boolean;
}

export async function uploadFile(
  args: UploadFileParams
): Promise<UploadFileResp> {
  const {
    baseDirectoryPath = [],
    file,
    useFilesystem,
    driveId,
    asyncUpload,
    reuseDuplicate,
  } = args;

  // Determine if this is a folder upload
  const isFolderUpload = !!(
    (file as any).webkitRelativePath || (file as any)._customRelativePath
  );

  // For folder creation with .empty files, we want the path to be just the folder path
  // without including the .empty filename, since the backend will extract the path correctly
  let uploadedFilePath: string;
  let actualFileName = file.name;
  let fileToUpload = file;

  // JSON array format: the backend preserves "/" in folder names.
  if (file.name === ".empty") {
    uploadedFilePath = JSON.stringify(baseDirectoryPath);
  } else {
    const relativePath =
      (file as any).webkitRelativePath || (file as any)._customRelativePath;
    if (relativePath) {
      // webkitRelativePath is the browser's own "/"-joined contract.
      const parts: string[] = relativePath.split("/");
      const baseFileName = parts[parts.length - 1];
      actualFileName = baseFileName;
      parts.pop(); // Remove filename
      // Combine baseDirectoryPath with relative folder structure
      uploadedFilePath = JSON.stringify([...baseDirectoryPath, ...parts]);
      fileToUpload = new File([file], actualFileName, {
        type: file.type,
        lastModified: file.lastModified,
      });
    } else {
      uploadedFilePath = JSON.stringify(baseDirectoryPath);
    }
  }

  const formData = new FormData();
  formData.append("uploaded_file_path", uploadedFilePath);
  formData.append("uploaded_file", fileToUpload);

  if (args.orgId) {
    formData.append("organizationId", args.orgId);
  } else if (args.workspaceId) {
    formData.append("workspaceId", args.workspaceId);
  }

  // Add driveId to form data if it exists
  if (driveId) {
    formData.append("drive_id", driveId);
  }

  if (useFilesystem) {
    formData.append("useFilesystem", "true");
  }
  if (isFolderUpload) {
    formData.append("isFolderUpload", "true");
  }
  if (reuseDuplicate) {
    formData.append("reuseDuplicate", "true");
  }
  formData.append("batchSize", String(args.batchSize));
  if (args.ingressBatch) formData.append("ingress_batch", args.ingressBatch);

  // Use asyncUpload endpoint if asyncUpload is true
  const uploadEndpoint = asyncUpload
    ? BASE_API_URL_V2 + "/api/v2/users/files/asyncUpload"
    : BASE_API_URL_V2 + "/api/v2/users/files/upload";

  const response = await fpFetch(uploadEndpoint, {
    method: "POST",
    body: formData,
  });

  // Skip refetch for async uploads since they don't return file info
  // The caller will handle cache updates manually
  if (!asyncUpload) {
    await queryClient.refetchQueries({ queryKey: ["orgFileList"] });
  }
  if (!response?.ok) {
    // Handle 409 Conflict with existing blob ID
    if (response?.status === 409) {
      try {
        const errorData = await response.json();
        if (errorData.existing_blob_id) {
          // Skip duplicate notification for .empty placeholder files used in
          // folder creation — a duplicate there just means the folder exists.
          if (file.name !== ".empty") {
            useFileTrayStore.getState().addDuplicateUpload({
              uploadedName: file.name,
              existingBlobId: errorData.existing_blob_id,
              existingName:
                errorData.existing_name || errorData.filename || file.name,
              existingPath: errorData.existing_path || [],
            });
          }
          return {
            _id: errorData.existing_blob_id,
            blob_id: errorData.existing_blob_id,
            filename: errorData.filename || file.name,
            duplicate: true,
            existing_path: errorData.existing_path || [],
            existing_name:
              errorData.existing_name || errorData.filename || file.name,
          };
        }
      } catch (parseError) {
        console.warn("Failed to parse 409 error response:", parseError);
      }
    }

    // Truthy, not `!== undefined`: a request that never reached the server
    // reports status 0, and naming it tells the reader nothing (#11634).
    const statusMessage = response?.status
      ? ` with status ${response.status}`
      : "";
    throw new Error(`Failed${statusMessage}`);
  }

  const data = await response.json();
  return data;
}
